declare global {
	interface Window {
		appTrackingAuthorized?: boolean;
		isNativeApp?: boolean;
		webkit?: {
			messageHandlers?: unknown;
		};
	}
}

/** UA token appended by the native app wrappers (Android + iOS) */
export const NATIVE_APP_UA_TOKEN = "AutomintApp";

/**
 * Detect if running inside an iOS WebView (WKWebView)
 */
export function detectIOSWebview(): boolean {
	if (typeof window === "undefined" || typeof navigator === "undefined")
		return false;

	try {
		const userAgent = navigator.userAgent;
		const isIOS = /iPad|iPhone|iPod/.test(userAgent);

		// Definitive signal: WKWebView exposes window.webkit.messageHandlers for the JS bridge.
		// Regular Safari and Chrome on iOS do NOT expose messageHandlers.
		const hasMessageHandlers =
			window.webkit !== undefined &&
			window.webkit.messageHandlers !== undefined;

		return isIOS && hasMessageHandlers;
	} catch {
		return false;
	}
}

/**
 * Detect if running inside one of the native app WebView wrappers
 * (automint-android / automint-ios) rather than a regular browser.
 */
export function detectNativeApp(): boolean {
	if (typeof window === "undefined" || typeof navigator === "undefined")
		return false;

	try {
		const userAgent = navigator.userAgent;

		// Custom token appended to the WebView UA by newer app versions
		if (userAgent.includes(NATIVE_APP_UA_TOKEN)) return true;

		// Stock Android System WebView marker; covers old Android app versions
		// where the injected window.isNativeApp flag can race hydration
		if (/; wv\)/.test(userAgent)) return true;

		// Flag injected by both wrappers (onPageStarted on Android,
		// WKUserScript at document start on iOS)
		if (window.isNativeApp === true) return true;

		return detectIOSWebview();
	} catch {
		return false;
	}
}

/**
 * Whether the surrounding native app can complete SSO via an in-app browser
 * popup (Chrome Custom Tabs / ASWebAuthenticationSession). Only app versions
 * that append the custom UA token support intercepting the OAuth URLs.
 */
export function supportsNativeSSO(): boolean {
	if (typeof navigator === "undefined") return false;

	return navigator.userAgent.includes(NATIVE_APP_UA_TOKEN);
}

/**
 * Set the tracking authorization status
 */
export function setTrackingAuthorization(authorized: boolean) {
	if (typeof window !== "undefined") {
		window.appTrackingAuthorized = authorized;

		window.dispatchEvent(
			new CustomEvent("trackingAuthorizationChanged", {
				detail: { authorized },
			}),
		);
	}
}

/**
 * Get the current tracking authorization status
 */
export function getTrackingAuthorization(): boolean {
	if (typeof window !== "undefined") {
		return window.appTrackingAuthorized ?? false;
	}
	return false;
}
